import { BUY } from "../constants/common";

const BRAND = "Gitcha";
const DEFAULT_TITLE = "Gitcha: Buyer Demand from Local Real Estate Agents";
const MAX_DESCRIPTION = 160;
const isPublicSyndication = (value) => value === true || value === 1 || value === "1";

const truncate = (text, max = MAX_DESCRIPTION) => {
  const value = String(text || "").replace(/\s+/g, " ").trim();
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).trimEnd()}…`;
};

// Rental want-listings share this page, so the consumer label follows adType.
// Anything unrecognised (e.g. land variants) stays neutral rather than guessing.
const consumerLabel = (adType) => {
  if (adType === "BUY") return "Buyer";
  if (adType === "LTR" || adType === "STR") return "Renter";
  return "";
};

const buildDescription = (adDetails) => {
  const desc = adDetails && adDetails.adDesc ? String(adDetails.adDesc).trim() : "";
  if (desc) return truncate(desc);
  const consumer = consumerLabel(adDetails && adDetails.adType);
  const listing = consumer ? `${consumer} Want-Listing` : "Want-Listing";
  const title = adDetails && adDetails.adTitle ? adDetails.adTitle : listing;
  return truncate(
    `${title}. A ${listing} from a local real estate agent on ${BRAND}.`,
  );
};

const buildAreaServed = (adDetails) => {
  const lat = Number(adDetails.lat);
  const lng = Number(adDetails.lng);
  const hasGeo =
    Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);
  const zipEntries = Array.isArray(adDetails.zipCode) ? adDetails.zipCode : [];
  const zips = zipEntries
    .map((entry) => entry?.zipCode)
    .filter(Boolean)
    .map((zip) => String(zip).trim());

  if (!hasGeo && zips.length === 0) return null;
  if (zips.length <= 1) {
    const place = { "@type": "Place" };
    if (hasGeo) {
      place.geo = { "@type": "GeoCoordinates", latitude: lat, longitude: lng };
    }
    if (zips[0]) {
      place.address = {
        "@type": "PostalAddress",
        postalCode: zips[0],
        addressCountry: "US",
      };
    }
    return place;
  }

  return zips.map((zip, index) => {
    const place = {
      "@type": "Place",
      address: { "@type": "PostalAddress", postalCode: zip, addressCountry: "US" },
    };
    if (index === 0 && hasGeo) {
      place.geo = { "@type": "GeoCoordinates", latitude: lat, longitude: lng };
    }
    return place;
  });
};

export const buildWantAdJsonLd = (
  adDetails,
  { origin = "", pathname = "", isPublicVisitor = false } = {},
) => {
  const indexable = isPublicVisitor === true && isPublicSyndication(adDetails?.syndicateToThePublic);
  if (!indexable) return null;

  const node = {
    "@context": "https://schema.org",
    "@type": "Demand",
    name: adDetails.adTitle || DEFAULT_TITLE,
    description: buildDescription(adDetails),
  };

  const canonical = origin ? `${origin}${pathname || ""}` : "";
  if (canonical) node.url = canonical;

  if (adDetails.adType === BUY) {
    node.businessFunction = "http://purl.org/goodrelations/v1#Buy";
  }

  const areaServed = buildAreaServed(adDetails);
  if (areaServed) node.areaServed = areaServed;

  return node;
};

export const buildWantAdSeo = (adDetails, { origin = "", pathname = "" } = {}) => {
  const indexable = isPublicSyndication(adDetails?.syndicateToThePublic);
  const title =
    adDetails && adDetails.adTitle ? `${adDetails.adTitle} | ${BRAND}` : DEFAULT_TITLE;
  const description = buildDescription(adDetails);
  const canonical = origin ? `${origin}${pathname || ""}` : "";
  const loaded = Boolean(adDetails);

  return {
    indexable,
    title,
    description,
    canonical,
    robots: loaded ? (indexable ? "index,follow" : "noindex,nofollow") : null,
    ogImage: origin ? `${origin}/logo512.png` : "",
  };
};

export default buildWantAdSeo;
